import { toast } from '../../../components/ui/toaster';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import {
  formatNotesAsSqlComments,
  formatWarningsAsSqlComments,
  getConnectionContext,
  type ScriptOperation,
  type SecurityExplorerNodeContext,
} from './securityExplorerNodeContext';
import { previewSecurityScriptForExplorerNode } from './securityExplorerScriptPreviewService';

export async function openSecurityScriptAsFromExplorerNode(
  context: SecurityExplorerNodeContext,
  operation: ScriptOperation,
): Promise<void> {
  const api = window.electronAPI?.securityManagement;
  if (!api) {
    toast.error({ title: 'Error', description: 'Security management API not available' });
    return;
  }

  const connectionContext = getConnectionContext(context.connectionId);
  if (!connectionContext) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { addTab, getNextQueryNumber, updateTabContent } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();
  const operationUpper = operation.toUpperCase();

  const tabId = addTab({
    type: 'query',
    title: `${context.principalName}_${operationUpper}-${queryNumber}`,
    content: {
      query: `-- Generating ${operationUpper} script for ${context.nodeType} ${context.principalName}...\n`,
      isGeneratingScript: true,
      resultSets: [],
      messages: [],
    },
    connectionInfo: {
      connectionId: context.connectionId,
      connectionName: connectionContext.connectionName,
      serverHost: connectionContext.serverHost,
      database: context.database ?? connectionContext.defaultDatabase,
    },
  });

  try {
    const notes: string[] = [];
    const result = await previewSecurityScriptForExplorerNode({ api, context, operation, notes });

    if (!result.success) throw new Error(result.error || 'Failed to generate script');

    const prelude = [
      formatWarningsAsSqlComments(result.warnings),
      formatNotesAsSqlComments(notes),
    ].filter(Boolean).join('\n');

    const script = result.script?.trim() ? result.script : '-- No script generated.';
    const query = prelude ? `${prelude}\n\n${script}\n` : `${script}\n`;

    updateTabContent(tabId, { query, isGeneratingScript: false });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to generate script';
    updateTabContent(tabId, {
      query: `-- Error generating script: ${message}\n`,
      isGeneratingScript: false,
    });
    toast.error({ title: 'Script Generation Error', description: message });
  }
}
